import { queryOptions, useMutation, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { toast } from 'sonner';
import {
  clearAdminTracingAiSummary,
  fetchAdminCompanyTracingOrders,
} from '@/api/(admin)/companies';
import type { AdminTracingOrderRow } from '@/api/(admin)/companies/types';
import { useAiSummaryBulkCreate } from '@/app/(client)/orders/-hooks/useAiSummaryBulkCreate';
import { TRACE_TYPE } from '@/app/(client)/orders/-types';
import { useTranslator } from '@/hooks/useTranslator';

interface Args {
  companyId: string;
  /** 1-based, as the table counts pages. */
  page: number;
  limit: number;
  sort?: string;
  /** Column → values, as the table holds them. */
  filters?: Record<string, string[]>;
}

/**
 * The tracing orders this company takes part in, and the AI re-run beside them.
 *
 * Paginates like the roster — a 0-based `page` and a `count` — not like the
 * network tab, which is the other convention in this feature.
 *
 * A re-run goes through the customer app's `useAiSummaryBulkCreate`: the queue
 * is the same one, and the trace type comes from the row, since this list is a
 * union of purchase and sales orders.
 */
/** Shared by the tab's route loader and the hook, so the prefetch warms the
 *  key the component reads. */
export function companyTracingOrdersQueryOptions({
  companyId,
  page,
  limit,
  sort,
  filters,
}: Args) {
  const qs = new URLSearchParams({
    page: String(Math.max(0, page - 1)),
    count: String(limit),
  });
  if (sort) qs.set('sort', sort);

  // One JSON object of `column: value`, matched with `LIKE` — so only the first
  // value per column is sent, as on the roster.
  const filter: Record<string, string> = {};
  for (const [key, values] of Object.entries(filters ?? {})) {
    const value = values?.[0];
    if (value) filter[key] = value;
  }
  if (Object.keys(filter).length > 0) qs.set('filter', JSON.stringify(filter));

  return queryOptions({
    queryKey: [
      'admin',
      'companies',
      'tracing-orders',
      companyId,
      page,
      limit,
      sort ?? null,
      filter,
    ] as const,
    queryFn: () => fetchAdminCompanyTracingOrders(companyId, qs.toString()),
    enabled: Boolean(companyId),
    staleTime: 60_000,
  });
}

export function useCompanyTracingOrders({
  companyId,
  page,
  limit,
  sort,
  filters,
}: Args) {
  const { getTranslation: t } = useTranslator({ page: 'managementCompanies' });
  const [rerunningOrderId, setRerunningOrderId] = useState<string | null>(null);
  const [clearingOrderId, setClearingOrderId] = useState<string | null>(null);

  const query = useQuery(
    companyTracingOrdersQueryOptions({
      companyId,
      page,
      limit,
      ...(sort ? { sort } : {}),
      ...(filters ? { filters } : {}),
    }),
  );
  const aiSummary = useAiSummaryBulkCreate();
  const clearMutation = useMutation({ mutationFn: clearAdminTracingAiSummary });

  async function rerunAnalysis(
    row: AdminTracingOrderRow,
    summaryTypes: string[],
  ): Promise<void> {
    setRerunningOrderId(row.id);
    try {
      await aiSummary.mutateAsync({
        ids: [row.id],
        // The list unions both order kinds, so the trace type is a property of
        // the row rather than of the page.
        traceType:
          row.isPurchaseOrder === 1
            ? TRACE_TYPE.PURCHASE_ORDER
            : TRACE_TYPE.SALES_ORDER,
        summaryTypes,
      });
      toast.success(t('detail.tracingOrders.rerunQueued'));
      await query.refetch();
    } catch (error) {
      toast.error(t('errors.rerunFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setRerunningOrderId(null);
    }
  }

  /**
   * Throws away one analysis. The old tab put this behind an eraser beside
   * each AI column — a wrong analysis had to be removable, not only re-run.
   */
  async function clearAnalysis(
    row: AdminTracingOrderRow,
    summaryType: 'docType' | 'docContent' | 'scAnalysis',
  ): Promise<void> {
    setClearingOrderId(row.id);
    try {
      await clearMutation.mutateAsync({
        tracingId: row.id,
        tracingOrder:
          row.isPurchaseOrder === 1 ? 'PURCHASE_ORDER' : 'SALES_ORDER',
        summaryType,
      });
      toast.success(t('detail.tracingOrders.analysisCleared'));
      await query.refetch();
    } catch (error) {
      toast.error(t('errors.clearAnalysisFailed'), {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      setClearingOrderId(null);
    }
  }

  return {
    rows: query.data?.data ?? [],
    totalCount: query.data?.metadata?.pagination?.total ?? 0,
    isLoading: query.isLoading,
    isError: query.isError,
    rerunAnalysis,
    rerunningOrderId,
    clearAnalysis,
    clearingOrderId,
  };
}
